import { Button, Input, Layout, Select } from "@vwfs-bronson/bronson-react"
import { FormikProps, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { FormField } from "../../../components/FormField";
import { StreetAutocomplete } from "../../../components/AddressAutocomplete/StreetAutocomplete";
import { ZipCodeAutocomplete } from "../../../components/AddressAutocomplete/ZipCodeAutocomplete";
import { countriesEU } from "../../../resources/base/models";

const FirstName = ({required = true}) => {
  const { t } = useTranslation()

  return (
    <FormField
      testId="firstName"
      type="input"
      name="customerData.deliveryAddress.deviatingFirstName"
      labelText={t('personal-details:firstName')}
      autoComplete="deliveryAddress-given-name"
      required={required}
      render={(fieldProps) => <Input {...fieldProps} />}
    />
  )
}

const LastName = ({required = true}) => {
  const { t } = useTranslation()

  return (
    <FormField
      testId="lastName"
      type="input"
      name="customerData.deliveryAddress.deviatingLastName"
      labelText={t('personal-details:lastName')}
      autoComplete="deliveryAddress-family-name"
      required={required}
      render={(fieldProps) => <Input {...fieldProps} />}
    />
  )
}

const CountryCode = () => {
  const { t } = useTranslation()

  return (
    <FormField
      testId="countryCode"
      type="select"
      name="customerData.deliveryAddress.countryCode"
      labelText={t('personal-details:countryCode')}
      autoComplete="country-name"
      required
      render={(fieldProps) => (
        <Select {...fieldProps}>
          {countriesEU?.map((country) => (
            country.value === 'DE' ?
            <Select.Item
              key={`${country.value}${country.label}`}
              selected={country.value === fieldProps.value}
              value={country.value}
            >
              {country.label}
            </Select.Item>
            : <></>
          ))}
        </Select>
      )}
    />
  )
}

const Street = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext()

  const isDisabled = () => {
    return (
      !(values as any)?.address?.[0]?.isManual &&
      !(values as any)?.address?.[0]?.city
    )
  }

  return (
    <FormField
      testId="street"
      type="input"
      name="customerData.deliveryAddress.street"
      labelText={t('personal-details:street')}
      autoComplete="deliveryAddress-line1"
      required
      render={(fieldProps) => <Input {...fieldProps} disabled={isDisabled()} />}
    />
  )
}

const HouseNumber = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext()

  const isDisabled = () => {
    return (
      !(values as any)?.address?.[0]?.isManual &&
      !(values as any)?.streetSelected
    )
  }

  return (
    <FormField
      testId="houseNumber"
      type="input"
      name="customerData.deliveryAddress.houseNumber"
      labelText={t('personal-details:houseNumber')}
      autoComplete="deliveryAddress-line2"
      required
      render={(fieldProps) => <Input {...fieldProps} disabled={isDisabled()} />}
    />
  )
}

const ZipCode = () => {
  const { t } = useTranslation()

  return (
    <FormField
      testId="zipCode"
      type="input"
      name="customerData.deliveryAddress.zipCode"
      labelText={t('personal-details:zipCode')}
      autoComplete="deliveryAddress-postal-code"
      required
      render={(fieldProps) => <Input {...fieldProps} />}
    />
  )
}

const City = () => {
  const { t } = useTranslation()
  const { values } = useFormikContext()
  const isManual = (values as any)?.address?.[0]?.isManual

  return (
    <FormField
      testId="city"
      type="input"
      name="customerData.deliveryAddress.city"
      labelText={t('personal-details:city')}
      autoComplete="deliveryAddress-level2"
      required
      render={(fieldProps) => <Input {...fieldProps} disabled={!isManual} />}
    />
  )
}

export const DeliveryAddressFieldset = () => {
  const { t } = useTranslation();
  const { setFieldValue, values } = useFormikContext() as FormikProps<any>;
  return (
    <Layout>
      <Layout.Item default="1/2" s="1/1">
        <FirstName />
      </Layout.Item>
      <Layout.Item default="1/2" s="1/1">
        <LastName />
      </Layout.Item>
      <Layout.Item default="1/2" s="1/1">
        <CountryCode />
      </Layout.Item>
      <Layout.Item default="1/2" s="1/1">
        <Button
          className="u-mt"
          type="button"
          link
          disabled={values?.address?.[0]?.isManual}
          onClick={(e: any) => {
            e.preventDefault()
            setFieldValue(`address[0].isManual`, true)
          }}
        >
          {t('personal-details:manualMode')}
        </Button>
      </Layout.Item>
      <Layout.Item default="1/2" s="1/1">
        {values?.address?.[0]?.isManual ? (
          <ZipCode />
        ) : (
          <ZipCodeAutocomplete
            prefixCode={`${values?.customerData?.deliveryAddress?.countryCode}`}
            addressName="deliveryAddress"
            label={t('personal-details:zipCode')}
          />
        )}
      </Layout.Item>
      <Layout.Item default="1/2" s="1/1">
        <City />
      </Layout.Item>
      <Layout.Item default="1/2" s="1/1" style={{ alignItems: 'baseline' }}>
        {values?.address?.[0]?.isManual ? (
          <Street />
        ) : (
          <StreetAutocomplete
            prefixCode={`${values?.customerData?.deliveryAddress?.countryCode}`}
            addressName="deliveryAddress"
            label={t('personal-details:street')}
          />
        )}
      </Layout.Item>
      <Layout.Item default="1/2" s="1/1">
        <HouseNumber />
      </Layout.Item>
    </Layout>
  )
}